import { type CSSProperties, type ReactNode } from 'react';
import { NavIcon } from '../../shell/shared';

// ─── Drawer Summary Banner ───

export function DrawerSummaryBanner({
  icon,
  iconBg,
  iconColor,
  value,
  description,
  breakdown,
}: {
  icon: string;
  iconBg: string;
  iconColor: string;
  value: string;
  description: string;
  breakdown?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3, 12px)',
        padding: 'var(--sp-4, 16px)',
        borderBottom: '1px solid var(--border, #e5e7eb)',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-lg, 14px)',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        <NavIcon name={icon} />
      </div>
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-xl, 24px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--text, #111827)',
            lineHeight: 1.2,
          }}
        >
          {value}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm, 13px)',
            color: 'var(--text-muted, #6b7280)',
            marginTop: 2,
          }}
        >
          {description}
        </div>
        {breakdown && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-muted, #6b7280)',
              marginTop: 2,
            }}
          >
            {breakdown}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Status Badge ───

const BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  // Encounter status
  open: { bg: '#dbeafe', color: '#2563eb' },
  'In Progress': { bg: '#dbeafe', color: '#2563eb' },
  closed: { bg: '#dcfce7', color: '#16a34a' },
  Completed: { bg: '#dcfce7', color: '#16a34a' },
  // Queue status
  serving: { bg: '#dcfce7', color: '#16a34a' },
  called: { bg: '#fef3c7', color: '#d97706' },
  waiting: { bg: '#f3f4f6', color: '#6b7280' },
  // Source
  Appointment: { bg: '#dbeafe', color: '#2563eb' },
  'Walk-in': { bg: '#fef3c7', color: '#d97706' },
  // Payment method
  cash: { bg: '#dcfce7', color: '#16a34a' },
  card: { bg: '#dbeafe', color: '#2563eb' },
  gcash: { bg: '#dbeafe', color: '#2563eb' },
  bank_transfer: { bg: '#f3e8ff', color: '#7c3aed' },
  'Credit Card': { bg: '#dbeafe', color: '#2563eb' },
  'Debit Card': { bg: '#dbeafe', color: '#2563eb' },
  Cash: { bg: '#dcfce7', color: '#16a34a' },
  Insurance: { bg: '#fef3c7', color: '#d97706' },
  GCash: { bg: '#dbeafe', color: '#2563eb' },
  // Alert
  critical: { bg: '#fecaca', color: '#dc2626' },
  Critical: { bg: '#fecaca', color: '#dc2626' },
  warning: { bg: '#fed7aa', color: '#ea580c' },
  Warning: { bg: '#fed7aa', color: '#ea580c' },
  info: { bg: '#dbeafe', color: '#2563eb' },
};

export function StatusBadge({ label, style }: { label: string; style?: CSSProperties }) {
  const colors = BADGE_COLORS[label] ?? { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full, 999px)',
        background: colors.bg,
        color: colors.color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {label}
    </span>
  );
}

// ─── Overdue Badge (gradient red→orange) ───

export function OverdueBadge({ days }: { days: number }) {
  const color = days >= 30 ? '#dc2626' : days >= 14 ? '#ea580c' : '#d97706';
  const bg = days >= 30 ? '#fecaca' : days >= 14 ? '#fed7aa' : '#fef3c7';
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-semi, 600)',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full, 999px)',
        background: bg,
        color: color,
        whiteSpace: 'nowrap',
      }}
    >
      {days} days
    </span>
  );
}

// ─── Patient Initials Avatar ───

const AVATAR_COLORS = [
  { bg: '#dbeafe', color: '#2563eb' },
  { bg: '#dcfce7', color: '#16a34a' },
  { bg: '#fef3c7', color: '#d97706' },
  { bg: '#fecaca', color: '#dc2626' },
  { bg: '#f3e8ff', color: '#7c3aed' },
  { bg: '#e0e7ff', color: '#4338ca' },
  { bg: '#ccfbf1', color: '#0d9488' },
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function PatientAvatar({ name, size = 36 }: { name: string | null; size?: number }) {
  const initials = (name ?? '??')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const palette = AVATAR_COLORS[hashStr(name ?? '') % AVATAR_COLORS.length];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-full, 999px)',
        background: palette.bg,
        color: palette.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.36,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ─── Drawer Row Card ───

export function DrawerRowCard({
  children,
  borderColor,
}: {
  children: ReactNode;
  borderColor?: string;
}) {
  return (
    <div
      style={{
        padding: 'var(--sp-4, 16px)',
        borderRadius: 'var(--radius-lg, 14px)',
        border: `1px solid ${borderColor ?? 'var(--border, #e5e7eb)'}`,
        background: 'var(--bg-surface, #fff)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2, 8px)',
      }}
    >
      {children}
    </div>
  );
}

// ─── Section Label ───

export function SectionLabel({ dot, label }: { dot?: string; label: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-semi, 600)',
        color: 'var(--text-muted, #6b7280)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 'var(--sp-2, 8px)',
      }}
    >
      {dot && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dot,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </div>
  );
}

// ─── Drawer Footer Button ───

export function DrawerFooterButton({
  label,
  color,
  onClick,
}: {
  label: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        height: 44,
        borderRadius: 'var(--radius-md, 10px)',
        border: 'none',
        background: color,
        color: '#fff',
        fontSize: 'var(--font-size-sm, 13px)',
        fontWeight: 'var(--font-weight-semi, 600)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      {label} →
    </button>
  );
}

// ─── Row CTA Button ───

export function RowCTAButton({
  label,
  variant = 'outline',
  color,
  onClick,
  icon,
}: {
  label: string;
  variant?: 'outline' | 'filled';
  color?: string;
  onClick: () => void;
  /** Trailing icon for the outline variant (defaults to a chevron). */
  icon?: string;
}) {
  const isFilled = variant === 'filled';
  return (
    <button
      onClick={onClick}
      style={{
        flex: isFilled ? 1 : undefined,
        height: 32,
        padding: '0 14px',
        borderRadius: 'var(--radius-md, 10px)',
        border: isFilled ? 'none' : '1px solid var(--border, #e5e7eb)',
        background: isFilled
          ? (color ?? 'var(--color-primary, #2563eb)')
          : 'var(--bg-surface, #fff)',
        color: isFilled ? '#fff' : 'var(--text, #111827)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {isFilled && <span>→</span>}
      {!isFilled && <NavIcon name={icon ?? 'chevron-right'} />}
    </button>
  );
}

// ─── Drawer Empty State ───

export function DrawerEmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        color: 'var(--text-placeholder, #9ca3af)',
        fontSize: 'var(--font-size-sm, 13px)',
        padding: 'var(--sp-8, 32px) var(--sp-4, 16px)',
      }}
    >
      {message}
    </div>
  );
}

// ─── Helpers ───

export function fmtDrawerTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function fmtDrawerRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (days === 0)
    return `Today, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function encDisplayId(id: string): string {
  return `ENC-${id.slice(-4).toUpperCase()}`;
}

export function paymentMethodLabel(method: string | null): string {
  if (!method) return 'N/A';
  const map: Record<string, string> = {
    cash: 'Cash',
    card: 'Credit Card',
    gcash: 'GCash',
    bank_transfer: 'Bank Transfer',
  };
  return map[method] ?? method;
}
